import usePagination from "~/hooks/usePagination";
import useCurrentUser from "~/hooks/user/useCurrentUser";
import useUrlParams from "~/hooks/useUrlParams";
import type { PaginationUrlParams } from "~/types";
import { toWholeCurrency } from "~/util/currency";
import { whereToAnd } from "~/util/payload";

/**
 * Interface for the parameters of the useGetPaginatedLeases function.
 * @interface
 * @property {string} contactId - The ID of the contact.
 * @property {number} depth - The depth of the data to fetch.
 * @property {boolean} filterOrganization - Whether to filter by organization.
 * @property {string} propertyId - The ID of the property.
 * @property {string} unitId - The ID of the unit.
 * @property {string} status - The status of the leases to fetch.
 */
interface Params {
  contactId?: string;
  depth?: number;
  filterOrganization?: boolean;
  propertyId?: string;
  unitId?: string;
  status?: string;
}

/**
 * Hook to fetch paginated lease data.
 * It first fetches the current user's data to get the organization ID,
 * then fetches the lease data using that ID.
 * @param {Params} params - The parameters for the hook.
 * @returns {Object} The lease data and loading state.
 */
const useGetPaginatedLeases = ({
  contactId,
  depth,
  filterOrganization,
  propertyId,
  unitId,
  status,
}: Params) => {
  const { params } = useUrlParams();

  const thisParamSet = params?.["leases"] as PaginationUrlParams;

  const where = {
    ...(thisParamSet?.where || {}),
  };

  const archived = thisParamSet?.where?.archived;

  delete where.archived;

  const {
    data: { organization },
    isLoading: userLoading,
  } = useCurrentUser();

  const organizationId = organization?.id;

  if (filterOrganization) {
    where.organization = {
      equals: organizationId,
    };
  }

  if (propertyId) {
    where.property = {
      equals: propertyId,
    };
  }

  if (unitId) {
    where.unit = {
      equals: unitId,
    };
  }

  if (contactId) {
    where.tenants = {
      equals: contactId,
    };
  }

  if (status) {
    where.status = {
      equals: status,
    };
  }

  const { data: hookData, ...rest } = usePagination({
    archived,
    collection: "leases",
    where: whereToAnd(where),
    sort: thisParamSet?.sort,
    page: thisParamSet?.page,
    limit: thisParamSet?.limit,
    serialLoading: userLoading,
    depth,
  });

  const allLeases = hookData.map((lease) => ({
    ...lease,
    amountDueRent: toWholeCurrency(lease.amountDueRent),
    securityDeposit: toWholeCurrency(lease.securityDeposit),
    rent: toWholeCurrency(lease.rent),
  }));

  return {
    filterEnabled: thisParamSet?.where,
    data: allLeases,
    ...rest,
  };
};

export default useGetPaginatedLeases;
